import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { pushDataLayer } from "@/lib/gtm";

export default function PortalLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "register" | "forgot">("login");
  const [forgotSent, setForgotSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const redirectTo = searchParams.get("redirect") || "/portal/dashboard";

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    else navigate(redirectTo);
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError("Bitte stimmen Sie den AGB und der Datenschutzerklärung zu.");
      return;
    }
    setLoading(true);
    setError("");
    const { data: signUpData, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) {
      setError(error.message);
    } else {
      // Store consent timestamp in profile after signup
      if (signUpData?.user?.id) {
        pushDataLayer("sign_up", { method: "email" });
        const now = new Date().toISOString();
        await supabase.from("profiles").upsert({
          id: signUpData.user.id,
          terms_accepted_at: now,
          privacy_accepted_at: now,
        } as any);
      }
      setError("Bitte bestätigen Sie Ihre E-Mail-Adresse.");
    }
    setLoading(false);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setError(error.message);
    else setForgotSent(true);
    setLoading(false);
  };

  const handleSubmit = mode === "login" ? handleLogin : mode === "register" ? handleRegister : handleForgot;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <img src="/favicon.svg" alt="NextInvoice" className="h-12" />
          </Link>
          <p className="text-muted-foreground">
            {mode === "login" && "Melden Sie sich in Ihrem Kundenportal an."}
            {mode === "register" && "Erstellen Sie Ihr Konto."}
            {mode === "forgot" && "Passwort zurücksetzen"}
          </p>
        </div>

        {mode === "login" && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <span>Registrieren Sie sich oder melden Sie sich mit Ihren Zugangsdaten an.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-card p-6 shadow-card space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail-Adresse</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@firma.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {mode !== "forgot" && (
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
          )}

          {mode === "register" && (
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                Ich habe die{" "}
                <Link to="/agb" target="_blank" className="text-primary hover:underline">AGB</Link>
                {" "}und die{" "}
                <Link to="/datenschutz" target="_blank" className="text-primary hover:underline">Datenschutzerklärung</Link>
                {" "}gelesen und stimme zu.
              </label>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
          {forgotSent && <p className="text-sm text-primary">Link zum Zurücksetzen wurde gesendet!</p>}

          <Button
            type="submit"
            className="w-full h-12 bg-primary text-primary-foreground"
            disabled={loading || (mode === "register" && !termsAccepted)}
          >
            {loading ? "Bitte warten…" : mode === "login" ? "Anmelden" : mode === "register" ? "Registrieren" : "Link senden"}
            {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-2 text-sm">
          {mode === "login" && (
            <>
              <button onClick={() => setMode("forgot")} className="text-muted-foreground hover:text-primary">
                Passwort vergessen?
              </button>
              <p className="text-muted-foreground">
                Noch kein Konto?{" "}
                <button onClick={() => { setMode("register"); setTermsAccepted(false); }} className="font-medium text-primary hover:underline">
                  Jetzt registrieren
                </button>
              </p>
            </>
          )}
          {mode === "register" && (
            <p className="text-muted-foreground">
              Bereits registriert?{" "}
              <button onClick={() => setMode("login")} className="font-medium text-primary hover:underline">
                Anmelden
              </button>
            </p>
          )}
          {mode === "forgot" && (
            <button onClick={() => setMode("login")} className="text-muted-foreground hover:text-primary">
              ← Zurück zum Login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
